/**
 * Historical Run View Page
 *
 * Shows evaluation results from a previously completed run,
 * including any human feedback that was submitted.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EvaluationAPI, EvaluationRunStatus, EvaluationResult } from '../../api/evaluation';
import { ResultsSection } from './ResultsSection';

export const HistoricalRun: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<EvaluationRunStatus | null>(null);
  const [results, setResults] = useState<EvaluationResult[]>([]);

  useEffect(() => {
    if (!runId) {
      setError('No run ID provided');
      setLoading(false);
      return;
    }

    const fetchRunData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch run status
        const status = await EvaluationAPI.getRunStatus(runId);
        setRunStatus(status);

        // Fetch results
        const resultsData = await EvaluationAPI.getRunResults(runId);
        setResults(resultsData);

        setLoading(false);
      } catch (err: any) {
        console.error('Failed to fetch historical run:', err);
        setError(err.message || 'Failed to load evaluation run');
        setLoading(false);
      }
    };

    fetchRunData();
  }, [runId]);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-gray-600">Loading evaluation run...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Run</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => navigate('/evaluation')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Back to Evaluation Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!runStatus) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">Run Not Found</h2>
          <p className="text-yellow-700 mb-4">The requested evaluation run could not be found.</p>
          <button
            onClick={() => navigate('/evaluation')}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
          >
            Back to Evaluation Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Historical Evaluation Run</h1>
          <button
            onClick={() => navigate('/evaluation')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Run Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Run ID:</span>
              <span className="ml-2 font-mono text-gray-900">{runId}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Dataset:</span>
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded">
                {runStatus.dataset_name}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Negotiation ID:</span>
              <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 rounded font-mono">
                {runStatus.negotiation_id}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded ${
                runStatus.status === 'COMPLETED'
                  ? 'bg-green-100 text-green-800'
                  : runStatus.status === 'FAILED'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {runStatus.status}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Items:</span>
              <span className="ml-2 text-gray-900">
                {runStatus.processed_items} / {runStatus.total_items}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Created:</span>
              <span className="ml-2 text-gray-900">
                {new Date(runStatus.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {runStatus.status === 'COMPLETED' && results.length > 0 ? (
        <ResultsSection results={results} readOnly={true} />
      ) : runStatus.status === 'COMPLETED' && results.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">No Results Available</h2>
          <p className="text-yellow-700">
            This evaluation run completed but has no results to display.
          </p>
        </div>
      ) : runStatus.status === 'FAILED' ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Evaluation Failed</h2>
          <p className="text-red-700">{runStatus.error_message || 'Unknown error'}</p>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-blue-800 mb-2">Evaluation In Progress</h2>
          <p className="text-blue-700">
            This evaluation is still running. Please check back later.
          </p>
        </div>
      )}
    </div>
  );
};
